import { useEffect, useMemo, useState } from 'react';
import { Container, Button, Typography, Checkbox, FormGroup, FormControlLabel, Box, Paper, Snackbar, Stepper, Step, StepLabel, Collapse, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { addEmployee, addEmployeeWorkHours, createAndStoreEmployeeSignUpInvite, fetchAllEmployees, fetchServices, generateRecurringBreakBlockersForEmployee, getCurrentBusinessConfig, getCurrentEmployee, updateEmployee, updateEmployeeScheduleForDate } from '../../firebase/firebase-delegate';
import { BitStringData, Employee, RecurringBreak, Service, WorkHours } from '../../types';
import ErrorDialog from '../../components/global/ErrorDialog';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import { TextField } from '@mui/material';
import { customAlphabet } from 'nanoid';
import { extractTimesFromBitString, generateScheduleBitString } from '../../time/timeUtils';
import { BUFFER_PERIOD, DAYJS_DAYS_OF_WEEK, DAYS_OF_WEEK_PREFERRED_ORDER, EMPLOYEE_BIO_MAX_LENGTH } from '../../constants';
import { dayjs } from '../../utils/dayjs-config';
import { arrayUnion, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { WEBSITE_ID } from '../../projectConfig';
import ScheduleCard from '../../components/employee/services/ScheduleCard';
import { hasDuplicateEmployeeEmail, normalizeEmail } from '../../utils/emailUtils';
import { convertBusinessHoursToWorkHours } from '../../utils/businessHours';
import ImageSelect from '../../components/global/images/ImageSelector';
import { EMPLOYEES_DB_REF } from '../../firebase/firebase-references';
import { resolveBookingUxConfig } from '../../utils/bookingUxConfig';

const parseFullName = (value: string): { firstName: string; lastName: string } => {
  const trimmed = value.trim();
  if (!trimmed) {
    return { firstName: '', lastName: '' };
  }
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
};

const AddEmployeePage = () => {
  const createStaffId = customAlphabet('ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789', 20);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [inviteLink, setInviteLink] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [bio, setBio] = useState('');
  const [profilePictureFile, setProfilePictureFile] = useState<File | undefined>(undefined);
  const [imageSelectResetKey, setImageSelectResetKey] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [showScheduleEditor, setShowScheduleEditor] = useState(false);
  const [openCreateSuccessDialog, setOpenCreateSuccessDialog] = useState(false);
  const [createdEmployeeName, setCreatedEmployeeName] = useState('');

  const employeeQuery = useQuery<Employee>({
    queryKey: ['currentEmployee'],
    queryFn: getCurrentEmployee
  });
  const servicesQuery = useQuery({ queryKey: ['services'], queryFn: () => fetchServices() });
  const businessConfigQuery = useQuery({ queryKey: ['businessConfig'], queryFn: () => getCurrentBusinessConfig() });
  const employeesQuery = useQuery({ queryKey: ['allEmployees'], queryFn: () => fetchAllEmployees() });

  const isLoading = employeeQuery.isLoading || servicesQuery.isLoading || businessConfigQuery.isLoading || employeesQuery.isLoading;
  const isError = employeeQuery.isError || servicesQuery.isError || businessConfigQuery.isError || employeesQuery.isError;


  const isOrchestratorMode = businessConfigQuery.data?.preferences?.orchestratorMode === true;
  const bookingUxConfig = resolveBookingUxConfig(businessConfigQuery.data);
  const showEmployeeBio = bookingUxConfig.uiFlags.showEmployeeBio;
  const showEmployeeProfilePhoto = bookingUxConfig.uiFlags.showEmployeeProfilePhoto;
  const isResourceMode = bookingUxConfig.bookingModel === 'resource';
  const resourceSingular = bookingUxConfig.terminology.resourceSingular;
  const resourcePlural = bookingUxConfig.terminology.resourcePlural;
  const employeeLimit = businessConfigQuery.data?.features?.employeeCount || 1;
  const loginlessEmployeeLimit = businessConfigQuery.data?.features?.loginlessEmployeeCount ?? employeeLimit;
  const employees = employeesQuery.data ?? [];
  const currentLoginEnabledEmployeeCount = employees.filter((employee) => employee.isOrchestrated !== true).length;
  const currentLoginlessEmployeeCount = employees.filter((employee) => employee.isOrchestrated === true).length;
  const isAtLoginEnabledLimit = !!employeeLimit && currentLoginEnabledEmployeeCount >= employeeLimit;
  const isAtLoginlessLimit = !!loginlessEmployeeLimit && currentLoginlessEmployeeCount >= loginlessEmployeeLimit;
  const isAtLimit = isOrchestratorMode ? isAtLoginlessLimit : isAtLoginEnabledLimit;
  const fullNameValue = `${firstName} ${lastName}`.trim();

  const [currentStep, setCurrentStep] = useState(0); // Step starts at 0
  const steps = ['1', '2', '3']; // Numbered steps

  const createInviteMutation = useMutation({
    mutationFn: async () => {
      return await createAndStoreEmployeeSignUpInvite(isAdmin, selectedServices);
    },
    onSuccess: (link) => setInviteLink(link),
    onError: (error) => {
      console.error('Error creating invite:', error);
      setShowErrorDialog(true)
    }
  });

  const isInviteLoading = createInviteMutation.isPending;
  const defaultWorkHours = useMemo(
    () => convertBusinessHoursToWorkHours(businessConfigQuery.data?.businessInfo?.businessHours),
    [businessConfigQuery.data?.businessInfo?.businessHours]
  );
  const [schedules, setSchedules] = useState<WorkHours>(defaultWorkHours);
  const [didHydrateBusinessHourDefaults, setDidHydrateBusinessHourDefaults] = useState(false);
  const [recurringBreakTemplates, setRecurringBreakTemplates] = useState<Record<keyof WorkHours, RecurringBreak[]>>(() =>
    DAYJS_DAYS_OF_WEEK.reduce((acc, day) => {
      acc[day] = [];
      return acc;
    }, {} as Record<keyof WorkHours, RecurringBreak[]>)
  );
  const templateEndDate = dayjs().add(3, 'month');

  useEffect(() => {
    if (didHydrateBusinessHourDefaults) {
      return;
    }
    if (!businessConfigQuery.data?.businessInfo?.businessHours) {
      return;
    }
    setSchedules(defaultWorkHours);
    setDidHydrateBusinessHourDefaults(true);
  }, [businessConfigQuery.data?.businessInfo?.businessHours, defaultWorkHours, didHydrateBusinessHourDefaults]);

  const flattenRecurringBreaks = () =>
    DAYJS_DAYS_OF_WEEK.flatMap((day, index) =>
      (recurringBreakTemplates[day] || []).map((definition) => ({
        ...definition,
        weekday: index,
      }))
    );

  const createLoginlessEmployeeMutation = useMutation({
    mutationFn: async () => {
      const trimmedFirstName = firstName.trim();
      const trimmedLastName = lastName.trim();
      const trimmedEmail = normalizeEmail(email);
      const trimmedPhone = isResourceMode ? '' : phone.trim();
      const trimmedBio = showEmployeeBio ? bio.trim().slice(0, EMPLOYEE_BIO_MAX_LENGTH) : '';
      const loginlessEmployeeLimit = businessConfigQuery.data?.features?.loginlessEmployeeCount
        ?? businessConfigQuery.data?.features?.employeeCount
        ?? 1;
      const latestEmployees = await fetchAllEmployees();
      const loginlessEmployees = latestEmployees.filter((employee) => employee.isOrchestrated === true);

      if (!trimmedFirstName) {
        throw new Error('First name is required.');
      }

      if (!!loginlessEmployeeLimit && loginlessEmployees.length >= loginlessEmployeeLimit) {
        throw new Error('Loginless employee limit exceeded');
      }

      if (!isResourceMode) {
        const duplicateEmail = hasDuplicateEmployeeEmail(latestEmployees, trimmedEmail);
        if (duplicateEmail) {
          throw new Error('An employee already exists with this email.');
        }
      }

      const employeeId = createStaffId();
      const profilePictureImagePath = (showEmployeeProfilePhoto && profilePictureFile)
        ? `${EMPLOYEES_DB_REF}${employeeId}/profilePicture`
        : undefined;
      const employee: Employee = {
        id: employeeId,
        admin: false,
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        email: isResourceMode ? '' : trimmedEmail,
        phone: trimmedPhone,
        bio: trimmedBio || undefined,
        profilePictureImagePath,
        appointments: {},
        isOrchestrated: true,
        active: isActive,
        recurringBreaks: flattenRecurringBreaks(),
      };

      await addEmployee(employee);
      if (showEmployeeProfilePhoto && profilePictureFile) {
        await updateEmployee(employee, employee, profilePictureFile);
      }
      await addEmployeeWorkHours(employeeId, schedules);

      const scheduleDates = Array.from({ length: BUFFER_PERIOD * 2 + 1 }, (_, index) => dayjs().add(index, 'day'));
      await Promise.all(
        scheduleDates.map((date) => updateEmployeeScheduleForDate(employeeId, schedules, date))
      );

      const windowStart = dayjs();
      const windowEnd = dayjs().add(BUFFER_PERIOD * 2, 'day');
      await generateRecurringBreakBlockersForEmployee({
        employeeId,
        windowStart,
        windowEnd,
        recurringBreakIdsToReset: flattenRecurringBreaks().map((definition) => definition.id),
      });

      if (selectedServices.length > 0) {
        await Promise.all(
          selectedServices.map((serviceId) =>
            updateDoc(
              doc(db, 'booking-websites', WEBSITE_ID, 'services', serviceId),
              { employees: arrayUnion(employeeId) }
            )
          )
        );
      }

      return { createdEmployee: employee, assignedServiceIds: [...selectedServices] };
    },
    onSuccess: ({ createdEmployee, assignedServiceIds }) => {
      setCreatedEmployeeName(`${createdEmployee.firstName} ${createdEmployee.lastName}`.trim());
      setOpenCreateSuccessDialog(true);
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setBio('');
      setProfilePictureFile(undefined);
      setImageSelectResetKey((previous) => previous + 1);
      setSelectedServices([]);
      setIsAdmin(false);
      setIsActive(true);
      setShowScheduleEditor(false);
      setSchedules(defaultWorkHours);
      setRecurringBreakTemplates(
        DAYJS_DAYS_OF_WEEK.reduce((acc, day) => {
          acc[day] = [];
          return acc;
        }, {} as Record<keyof WorkHours, RecurringBreak[]>)
      );
      queryClient.setQueryData<Employee[]>(['allEmployees'], (previous = []) => {
        if (previous.some((employee) => employee.id === createdEmployee.id)) {
          return previous;
        }
        return [...previous, createdEmployee];
      });
      queryClient.setQueryData<Employee[]>(['employees'], (previous = []) => {
        if (previous.some((employee) => employee.id === createdEmployee.id)) {
          return previous;
        }
        return [...previous, createdEmployee];
      });
      if (assignedServiceIds.length > 0) {
        queryClient.setQueryData<Service[]>(['services'], (previous = []) => (
          previous.map((service) => {
            if (!assignedServiceIds.includes(service.id)) {
              return service;
            }
            const employeeSet = new Set(service.employees ?? []);
            employeeSet.add(createdEmployee.id);
            return {
              ...service,
              employees: Array.from(employeeSet),
            };
          })
        ));
      }
      queryClient.invalidateQueries({ queryKey: ['allEmployees'] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['services'] });
      employeesQuery.refetch();
    },
    onError: (error) => {
      console.error('Error creating loginless employee profile:', error);
      setShowErrorDialog(true);
    },
  });

  const handleCreateSuccessDialogClose = () => {
    setOpenCreateSuccessDialog(false);
    navigate('/admin/manage-employees');
  };

  const handleServiceToggle = (serviceId: string) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId) ? prev.filter(id => id !== serviceId) : [...prev, serviceId]
    );
  };

  const handleScheduleChange = (day: keyof WorkHours, updatedSchedule: BitStringData) => {
    setSchedules((prevSchedules) => ({
      ...prevSchedules,
      [day]: generateScheduleBitString(updatedSchedule),
    }));
  };

  const handleRecurringBreaksChange = (day: keyof WorkHours, breaks: RecurringBreak[]) => {
    setRecurringBreakTemplates((prev) => ({
      ...prev,
      [day]: breaks,
    }));
  };

  const handleProfilePictureChange = (newPicture?: File) => {
    setProfilePictureFile(newPicture);
  };

  const handleCopyLink = async () => {
    if (inviteLink) {
      await navigator.clipboard.writeText(inviteLink);
      setSnackbarMessage('Invite link copied to clipboard!');
      setSnackbarOpen(true);
    }
  };

  const handleShare = async () => {
    if (inviteLink && navigator.share) {
      try {
        await navigator.share({ title: 'Employee Invite', text: 'Join our system using this invite link:', url: inviteLink });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      setSnackbarMessage('Sharing not supported on this device.');
      setSnackbarOpen(true);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <Typography
              variant="overline"
              align="center"
              display="block"
              sx={{ marginBottom: 2 }}
            >
              SELECT ROLE
            </Typography>
            <Typography variant="body1" gutterBottom sx={{ maxWidth: 400 }}>
              {`${resourcePlural} with admin access can manage other ${resourcePlural.toLowerCase()}, services and appointments.`}
            </Typography>
            <FormControlLabel
              control={<Checkbox checked={isAdmin} onChange={() => setIsAdmin(!isAdmin)} />}
              label="Give admin access"
              sx={{ mt: 2 }}
            />
          </Box>
        );
      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography
              variant="overline"
              align="center"
              display="block"
              sx={{ marginBottom: 2 }}
            >
              SELECT SERVICES
            </Typography>
            {servicesQuery.isLoading ? (
              <LoadingSpinner />
            ) : (
              <FormGroup>
                {servicesQuery.data?.map((service: Service) => (
                  <FormControlLabel
                    key={service.id}
                    control={<Checkbox checked={selectedServices.includes(service.id)} onChange={() => handleServiceToggle(service.id)} />}
                    label={service.title}
                  />
                ))}
              </FormGroup>
            )}
          </Box>
        );
      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxHeight: '300px' }}>
            <Typography
              variant="overline"
              align="center"
              display="block"
              sx={{ marginBottom: 2 }}
            >
              SEND INVITE
            </Typography>

            {isInviteLoading ? (
              <LoadingSpinner />
            ) : (
              inviteLink && (
                <Button
                  variant="outlined"
                  onClick={handleCopyLink}
                  sx={{ mb: 2 }}
                >
                  {inviteLink}
                </Button>
              )
            )}

            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
              Expires in two hours
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Failed to load data
          </Typography>
          <Typography>Please refresh the page or try again later.</Typography>
        </Paper>
      </Container>
    );
  }

  if (isAtLimit) {
    const limitTitle = isOrchestratorMode ? 'Loginless Employee Limit Reached' : 'Employee Login Limit Reached';
    const limitDescription = isOrchestratorMode
      ? 'You cannot add more loginless employees with this current plan.'
      : 'You cannot add more login-enabled employees with this current plan.';

    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {limitTitle}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {limitDescription}
            <br />
            Please contact the <strong>Scheduify</strong> team to upgrade your website package.
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (isOrchestratorMode) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="h5" align="center" fontWeight="bold" gutterBottom>
              {`Add ${resourceSingular} Profile`}
            </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {showEmployeeProfilePhoto && (
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center' }}>
                  Profile Picture (Optional)
                </Typography>
                <ImageSelect
                  key={imageSelectResetKey}
                  onImageChange={handleProfilePictureChange}
                  allowDelete
                  styleType="circle"
                  altText={`${firstName || resourceSingular} ${lastName || ''}`.trim()}
                />
              </Box>
            )}
            {showEmployeeBio && (
              <TextField
                label="Bio (Optional)"
                value={bio}
                onChange={(event) => setBio(event.target.value.slice(0, EMPLOYEE_BIO_MAX_LENGTH))}
                placeholder="Add a short profile blurb."
                helperText={`${bio.length}/${EMPLOYEE_BIO_MAX_LENGTH}`}
                multiline
                minRows={3}
                inputProps={{ maxLength: EMPLOYEE_BIO_MAX_LENGTH }}
                sx={{ width: '100%', mt: 1, mb: 2 }}
              />
            )}

            {isResourceMode ? (
              <TextField
                label="Name"
                value={fullNameValue}
                onChange={(event) => {
                  const parsed = parseFullName(event.target.value);
                  setFirstName(parsed.firstName);
                  setLastName(parsed.lastName);
                }}
                sx={{ width: '100%', mt: 1, mb: 2 }}
              />
            ) : (
              <>
                <TextField
                  label="First Name"
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                  sx={{ width: '100%', mt: 1, mb: 2 }}
                />
                <TextField
                  label="Last Name"
                  value={lastName}
                  onChange={(event) => setLastName(event.target.value)}
                  sx={{ width: '100%', mb: 2 }}
                />
              </>
            )}
            {!isResourceMode && (
              <>
                <TextField
                  label="Email (optional)"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  sx={{ width: '100%', mb: 2 }}
                />
                <TextField
                  label="Phone (optional)"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  sx={{ width: '100%', mb: 2 }}
                />
              </>
            )}

            <Typography variant="overline" align="center" display="block" sx={{ mb: 1 }}>
              SELECT SERVICES
            </Typography>
            <FormGroup sx={{ width: '100%', textAlign: 'left' }}>
              {servicesQuery.data?.map((service: Service) => (
                <FormControlLabel
                  key={service.id}
                  control={<Checkbox checked={selectedServices.includes(service.id)} onChange={() => handleServiceToggle(service.id)} />}
                  label={service.title}
                />
              ))}
            </FormGroup>

            <Box sx={{ width: '100%', mt: 2, textAlign: 'left' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                New employee work hours default to your business hours.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => setShowScheduleEditor((prev) => !prev)}
                  sx={{
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 3,
                    borderRadius: 2,
                    borderWidth: 2,
                    '&:hover': { borderWidth: 2 },
                  }}
                >
                  {showScheduleEditor ? 'Hide Edit Work Hours' : 'Edit Work Hours'}
                </Button>
              </Box>
              <Collapse in={showScheduleEditor}>
                {DAYS_OF_WEEK_PREFERRED_ORDER.map((day) => {
                  const parsed = extractTimesFromBitString(schedules[day]);
                  return (
                    <ScheduleCard
                      key={day}
                      day={day}
                      initialWorkHours={parsed}
                      onScheduleChange={handleScheduleChange}
                      recurringBreaks={recurringBreakTemplates[day]}
                      onRecurringBreaksChange={handleRecurringBreaksChange}
                      templateEndDate={templateEndDate}
                    />
                  );
                })}
              </Collapse>
            </Box>
            <FormControlLabel
              control={<Checkbox checked={isActive} onChange={() => setIsActive(!isActive)} />}
              label="Active"
            />

            <Button
              variant="contained"
              color="secondary"
              onClick={() => createLoginlessEmployeeMutation.mutate()}
              disabled={createLoginlessEmployeeMutation.isPending}
              sx={{ width: '100%', mt: 2, borderRadius: '4px', fontWeight: '600' }}
            >
              {createLoginlessEmployeeMutation.isPending ? 'Creating...' : `Create ${resourceSingular} Profile`}
            </Button>
          </Box>
        </Paper>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
        <ErrorDialog
          open={showErrorDialog}
          onClose={() => setShowErrorDialog(false)}
          message={"Something went wrong! Please try again later, or contact support."}
        />
        <Dialog open={openCreateSuccessDialog} onClose={handleCreateSuccessDialogClose}>
          <DialogTitle>{`${resourceSingular} Created`}</DialogTitle>
          <DialogContent>
            <Typography>
              {createdEmployeeName
                ? `${createdEmployeeName} was created successfully.`
                : `${resourceSingular} profile created successfully.`}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" color="secondary" onClick={handleCreateSuccessDialogClose}>
              {`Go To Manage ${bookingUxConfig.terminology.resourcePlural}`}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
        <Typography variant="h5" align="center" fontWeight="bold" gutterBottom>
          {`Add ${resourceSingular}`}
        </Typography>

        <Stepper activeStep={currentStep} alternativeLabel sx={{ my: 2 }}>
          {steps.map((_, index) => (
            <Step key={index}>
              <StepLabel></StepLabel> {/* Not adding labels in the steppers; will display numbers like 1,2,3...*/}
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}

        <Box sx={{ mt: 3, mb: 2, display: 'flex', justifyContent: 'space-between' }}>
          {currentStep < steps.length && (
            <Button
              variant="outlined"
              onClick={() => setCurrentStep((prev) => prev - 1)}
              disabled={currentStep === 0}
              sx={{ borderRadius: '4px', border: '2px solid #FFF', fontWeight: '600' }}
            >
              Back
            </Button>
          )}
          {/* No next button in the last step*/}
          {currentStep < steps.length - 1 && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                if (currentStep === 1) {
                  createInviteMutation.mutate();
                }
                setCurrentStep((prev) => prev + 1);
              }}
              sx={{ borderRadius: '4px', border: '2px solid rgba(255,255,255,0.15)', fontWeight: '600' }}
            >
              Next
            </Button>
          )}

          {currentStep === steps.length - 1 && !isInviteLoading && inviteLink && (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleShare}
              sx={{ borderRadius: '4px', border: '2px solid rgba(255,255,255,0.15)', fontWeight: '600' }}
            >
              Share
            </Button>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
      <ErrorDialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        message={"Something went wrong! Please try again later, or contact support."}
      />
    </Container>
  );

};

export default AddEmployeePage;
